import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subject, from } from 'rxjs';
import { filter, map, skipWhile, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { SharedAnalyticsService } from '../../shared/analytics/shared-analytics/shared-analytics.service';
import { EAnalyticsPageViews } from '../../shared/enums/analytics.enum';
import { GTMEventsCrash } from '../../shared/enums/Crashlytics';
import { NativeStorageNamesV2, SECTIONS, TypesErrors } from '../../shared/models/Constants';
import { IErrorScreen } from '../../shared/models/Error';
import { ILegacyDocument } from '../../shared/models/Legales';
import { CrashlyticsService } from '../../shared/services/crashlytics/crashlytics.service';
import { DispatchInitService } from '../../shared/services/dispatch-init/dispatch-init.service';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import { StorageData } from '../../shared/services/utilities/storage-data.class';
import { clearGetPrivacyNotice, clearGetTermsAndConditions, getPrivacyNotice, getTermsAndConditions } from '../../store/actions';
import { AppState } from '../../store/app-reducers';
import { LegalGetState } from '../../store/reducers';
import { selectPrivacyNotice, selectTermsAndConditions } from '../../store/selectors/content-management.selectors';


@Component({
  selector: 'app-terminos',
  templateUrl: './terminos.page.html',
  styleUrls: ['./terminos.page.scss'],
})
export class TerminosPage implements OnInit, OnDestroy {

  @Input() tipo = '';
  @Input() boton = 'ACEPTAR';

  public backDefaultHref = 'main/Mas';
  public tipoError: IErrorScreen | null = null;
  public hasError = false;
  public byUrl = false;
  public dataStoragePoliticas: ILegacyDocument = { ID_LegalTipo: 0, Descripcion: '', Documento: '', Version: '' };

  private destroy$ = new Subject<void>();
  private storagePoliticas = '';

  constructor(
    private sharedService: SharedServiceService
    , private dispatchInitS: DispatchInitService
    , private crashlytics: CrashlyticsService
    , private modalCtrl: ModalController
    , private actdRoute: ActivatedRoute
    , private sharedAnalyticsS: SharedAnalyticsService
    , private store: Store<AppState>
  ) { }

  ngOnInit() {
    this.actdRoute.params
      .pipe(
        map(value => value.document),
        // 1. Manejamos el cambio de ruta y configuramos las variables de sincronización
        tap(route => this.setupRouteVariables(route)),
        // 2. Convertimos el flujo al flujo legal correspondiente
        switchMap(() => this.processLegalFlow()),
        takeUntil(this.destroy$)
      )
      .subscribe(this.callbackSuccessPoliticas);
  }

  private setupRouteVariables(route: string) {
    this.dispatchInitS.stopInitAfterView = route === 'terminos-condiciones' || route === 'aviso-privacidad';

    if (route === 'terminos-condiciones') {
      this.tipo = 'isTerminos';
      this.byUrl = true;
    } else if (route === 'aviso-privacidad') {
      this.tipo = 'isAviso';
      this.byUrl = true;
    }
  }

  private processLegalFlow() {
    const isTerms = this.tipo === 'isTerminos';
    this.storagePoliticas = isTerms ? NativeStorageNamesV2.TerminosCondiciones : NativeStorageNamesV2.AvisoPrivacidad;
    const selector = isTerms ? selectTermsAndConditions : selectPrivacyNotice;
    const action = isTerms ? getTermsAndConditions() : getPrivacyNotice();

    // Convertimos la Promesa del storage en un Observable
    return from(StorageData.getDataStorage(this.storagePoliticas)).pipe(
      tap(legalStg => {
        if (legalStg) {
          this.dataStoragePoliticas = legalStg;
        }
      }),
      tap(() => this.store.dispatch(action)),
      // Conmutamos al selector del store (switchMap cancelará el anterior si la ruta cambia)
      switchMap(() => this.store.select(selector)),
      filter(state => !!state),
      skipWhile(state => !state.success && !state.failed),
      // Usamos take(1) si solo esperas la primera respuesta exitosa/fallida para evitar que se quede escuchando eternamente
      take(1)
    );
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.sharedService.loadingDismissActive();
    this.store.dispatch(clearGetPrivacyNotice());
    this.store.dispatch(clearGetTermsAndConditions());
  }

  ionViewDidEnter() {
    if (!this.tipo) return;
    const view = this.tipo === 'isTerminos' ? EAnalyticsPageViews.termsAndConditionsView : EAnalyticsPageViews.noticeOfPrivacyView
    this.sharedAnalyticsS.trackPageView(view, SECTIONS.LEGALES);
  }

  private callbackSuccessPoliticas = async (resp: LegalGetState) => {
    if (!resp) return;
    this.sharedService.loadingDismissActive();

    if (resp.success) {
      this.dataStoragePoliticas = resp.data;
      StorageData.setDataStorage(this.storagePoliticas, resp.data);
    }

    if (resp.failed) {
      this.tipoError = TypesErrors.WeHaveDifficulties;
      this.hasError = true;
      await this.crashlytics.sendCrashPlatform(GTMEventsCrash.getLegalesApi, resp.error);
    }
  }

  public backTrue = () => {
    this.modalCtrl.dismiss(true);
  }

  public backFalse = () => {
    this.modalCtrl.dismiss(false);
  }

}
